import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { VISIT_SOURCES } from "./visit-source";

/**
 * Zprávy a e-maily, které doporučitel dostane z mapy.
 *
 * Každá zpráva se zároveň zapíše do `mapa_access_log` jako návštěva se zdrojem
 * `chat` (zpráva v aplikaci) nebo `email`, takže se v adminu Doporučení i v
 * popupech mapy počítá do aktivace uživatele.
 */

const SendSchema = z.object({
  referrerUserId: z.string().uuid(),
  businessId: z.string().uuid().optional().nullable(),
  senderName: z.string().max(120).optional().nullable(),
  senderEmail: z.string().email().max(320).optional().nullable(),
  message: z.string().min(3).max(2000),
  /** `message` = zpráva v aplikaci (chat), `email` = poslat i e-mailem. */
  channel: z.enum(["message", "email"]).optional().default("message"),
  source: z.enum(VISIT_SOURCES).optional().default("app"),
  lang: z.string().max(10).optional().nullable(),
});

export const sendReferrerMessage = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => SendSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Jednoduchá ochrana proti zahlcení: max 5 zpráv za hodinu na doporučitele
    // z jednoho odesílatelského e-mailu (nebo bez e-mailu 5 celkem).
    const since = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    const { count } = await supabaseAdmin
      .from("referrer_messages")
      .select("id", { count: "exact", head: true })
      .eq("referrer_user_id", data.referrerUserId)
      .gte("created_at", since);
    if ((count ?? 0) >= 20) {
      return { ok: false as const, reason: "rate_limited" as const };
    }

    // Návštěva se zdrojem podle kanálu → aktivace v Doporučení i na mapě.
    const visitSource = data.channel === "email" ? "email" : "chat";
    const { data: logRow } = await supabaseAdmin
      .from("mapa_access_log")
      .insert({
        result: "allowed",
        path: "/mapa",
        lang: data.lang ?? null,
        email: data.senderEmail ?? null,
        user_id: data.referrerUserId,
        source: visitSource,
      })
      .select("id")
      .maybeSingle();

    const { data: row, error } = await supabaseAdmin
      .from("referrer_messages")
      .insert({
        referrer_user_id: data.referrerUserId,
        business_id: data.businessId ?? null,
        sender_name: data.senderName ?? null,
        sender_email: data.senderEmail ?? null,
        message: data.message,
        channel: data.channel,
        source: data.source,
        access_log_id: (logRow as { id?: string } | null)?.id ?? null,
      })
      .select("id")
      .maybeSingle();
    if (error) throw new Error(error.message);

    // E-mailová varianta: doručíme doporučiteli na jeho registrovaný e-mail.
    let delivery: DeliveryStatus = "n_a";
    if (data.channel === "email" && row) {
      delivery = await deliverMessageEmail((row as { id: string }).id);
    }

    return {
      ok: true as const,
      id: (row as { id: string } | null)?.id ?? null,
      delivery,
    };
  });

export type DeliveryStatus = "n_a" | "pending" | "sent" | "failed" | "skipped";

/** Odešle (nebo znovu odešle) e-mail ke konkrétní zprávě a zapíše stav doručení. */
async function deliverMessageEmail(messageId: string): Promise<DeliveryStatus> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: msg } = await supabaseAdmin
    .from("referrer_messages")
    .select("id, referrer_user_id, sender_name, sender_email, message, delivery_attempts")
    .eq("id", messageId)
    .maybeSingle();
  if (!msg) return "failed";
  const m = msg as {
    id: string; referrer_user_id: string; sender_name: string | null;
    sender_email: string | null; message: string; delivery_attempts: number | null;
  };

  const { data: profile } = await supabaseAdmin
    .from("profiles")
    .select("email, full_name")
    .eq("id", m.referrer_user_id)
    .maybeSingle();
  const p = profile as { email?: string | null; full_name?: string | null } | null;
  const to = p?.email ?? null;

  const attempts = (m.delivery_attempts ?? 0) + 1;
  const base = {
    delivery_attempts: attempts,
    last_attempt_at: new Date().toISOString(),
  };

  if (!to) {
    await supabaseAdmin
      .from("referrer_messages")
      .update({ ...base, delivery_status: "failed", delivery_error: "Doporučitel nemá e-mail v profilu" })
      .eq("id", m.id);
    return "failed";
  }

  try {
    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    const res = await sendTemplateEmail("referrer-message", to, {
      templateData: {
        recipientName: p?.full_name ?? "",
        senderName: m.sender_name ?? "Návštěvník mapy",
        senderEmail: m.sender_email ?? "",
        message: m.message,
        lang: "cs",
      },
      replyTo: m.sender_email ?? undefined,
    });
    const status: DeliveryStatus = res.sent ? "sent" : "skipped";
    await supabaseAdmin
      .from("referrer_messages")
      .update({
        ...base,
        delivery_status: status,
        delivery_error: res.sent ? null : "Příjemce je na seznamu blokovaných adres",
        emailed_at: res.sent ? new Date().toISOString() : null,
      })
      .eq("id", m.id);
    return status;
  } catch (e) {
    await supabaseAdmin
      .from("referrer_messages")
      .update({
        ...base,
        delivery_status: "failed",
        delivery_error: e instanceof Error ? e.message : "Neznámá chyba odeslání",
      })
      .eq("id", m.id);
    return "failed";
  }
}

type Ctx = {
  supabase: { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }> };
  userId: string;
};

async function assertAdmin(context: Ctx) {
  const { data, error } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

export type ReferrerMessageRow = {
  id: string;
  referrer_user_id: string;
  referrer_name: string | null;
  business_id: string | null;
  business_name: string | null;
  sender_name: string | null;
  sender_email: string | null;
  channel: string;
  source: string;
  message: string;
  status: string;
  emailed_at: string | null;
  delivery_status: string;
  delivery_error: string | null;
  delivery_attempts: number;
  last_attempt_at: string | null;
  created_at: string;
};

export const listReferrerMessages = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ limit: z.number().int().min(1).max(200).optional().default(50) }).parse(d ?? {}))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as unknown as Ctx);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: rows, error } = await supabaseAdmin
      .from("referrer_messages")
      .select("id, referrer_user_id, business_id, sender_name, sender_email, channel, source, message, status, emailed_at, delivery_status, delivery_error, delivery_attempts, last_attempt_at, created_at")
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (error) throw new Error(error.message);

    const list = (rows ?? []) as Array<Omit<ReferrerMessageRow, "referrer_name" | "business_name">>;
    const userIds = [...new Set(list.map((r) => r.referrer_user_id))];
    const bizIds = [...new Set(list.map((r) => r.business_id).filter(Boolean))] as string[];

    const [profRes, bizRes] = await Promise.all([
      userIds.length
        ? supabaseAdmin.from("profiles").select("id, full_name, email").in("id", userIds)
        : Promise.resolve({ data: [] as unknown[] }),
      bizIds.length
        ? supabaseAdmin.from("businesses").select("id, name").in("id", bizIds)
        : Promise.resolve({ data: [] as unknown[] }),
    ]);

    const nameById = new Map(
      ((profRes.data ?? []) as Array<{ id: string; full_name: string | null; email: string | null }>).map(
        (p) => [p.id, p.full_name ?? p.email],
      ),
    );
    const bizById = new Map(
      ((bizRes.data ?? []) as Array<{ id: string; name: string }>).map((b) => [b.id, b.name]),
    );

    const enriched: ReferrerMessageRow[] = list.map((r) => ({
      ...r,
      referrer_name: nameById.get(r.referrer_user_id) ?? null,
      business_name: r.business_id ? bizById.get(r.business_id) ?? null : null,
    }));

    const unread = enriched.filter((r) => r.status === "new").length;
    const failed = enriched.filter((r) => r.delivery_status === "failed").length;
    return { rows: enriched, unread, failed };
  });

/** Rychlé znovuodeslání e-mailu u zprávy, jejíž doručení selhalo. */
export const resendReferrerMessageEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as unknown as Ctx);
    const delivery = await deliverMessageEmail(data.id);
    return { ok: delivery === "sent", delivery };
  });

/** Veřejné potvrzení, že se zpráva zaevidovala (pro popup na mapě). */
export const checkReferrerMessageLogged = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("referrer_messages")
      .select("id, access_log_id, delivery_status")
      .eq("id", data.id)
      .maybeSingle();
    const r = row as { access_log_id: string | null; delivery_status: string } | null;
    return {
      logged: !!r,
      countedAsVisit: !!r?.access_log_id,
      delivery: r?.delivery_status ?? null,
    };
  });

export const markReferrerMessageStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid(), status: z.enum(["new", "read", "archived"]) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as unknown as Ctx);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("referrer_messages")
      .update({ status: data.status })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
